// Single source of truth for subscription tiers — used by the public Pricing page now,
// and by Stripe billing + limit enforcement in Phase B. Prices in EUR (FR market first).
//
// MODEL (Operately-style flat tiers, confirmed with owner):
//  - SEATS: each plan INCLUDES a FIXED cap of "collaborator" seats (limits.collaborators) =
//    members, managers, admins, super_admins (people who can act). NO per-seat overage — to
//    add more users you UPGRADE to a higher tier. VIEWERS (read-only) are ALWAYS FREE and
//    UNLIMITED (limits.viewers=null). Caps: Free 3 · Pro 30 · Business 100 · Portfolio/Enterprise ∞.
//  - "entities" = workspaces / HQs. "spaces" = sub-areas inside an entity. null = unlimited.
//  - limits.advisory: false (none) | "review" (performance/weekly/monthly reviews only)
//                     | "full" (review + interactive Board meetings).  Board mode = Business+.
//  - Business is the DEFAULT / most-popular choice (popular: true).
//  - Billing: monthly (price) OR annual (priceAnnual = total €/year, −20%) OR lifetime
//    (priceLifetime = 48× monthly −50%).
//  - limits.aiCredits: number | "custom".  limits.storageGb: number | null (custom).
//
// Server-authoritative mirror lives in backend/core/billing.py (PLAN_LIMITS / PLAN_PRICES).

// 7-day free trial of the Business plan (no credit card). New accounts start here.
export const TRIAL = { days: 7, planId: "business" };

export const PLANS = [
  {
    id: "free",
    price: 0,
    priceAnnual: 0,
    popular: false,
    limits: { entities: 1, spaces: 3, collaborators: 3, viewers: null, advisory: false, aiCredits: 25, automations: 2, storageGb: 10, advancedPermissions: false, onboarding: false },
    name: { fr: "Solo", en: "Solo" },
    framing: { fr: "Pour les indépendants et petites équipes qui démarrent", en: "For freelancers and small teams getting started" },
    tagline: { fr: "Le strict nécessaire pour piloter une activité.", en: "The essentials to run one activity." },
    cta: { fr: "Commencer gratuitement", en: "Start free" },
    features: {
      fr: ["1 entité / espace de travail", "3 espaces", "3 collaborateurs", "Lecteurs illimités et gratuits", "25 crédits IA / mois", "10 Go de stockage"],
      en: ["1 entity / workspace", "3 spaces", "3 collaborators", "Unlimited free viewers", "25 AI credits / month", "10 GB storage"],
    },
  },
  {
    id: "pro",
    price: 49,
    priceAnnual: 470,
    priceLifetime: 1176,
    popular: false,
    limits: { entities: 3, spaces: null, collaborators: 30, viewers: null, advisory: "review", aiCredits: 300, automations: 10, storageGb: 100, advancedPermissions: false, onboarding: false },
    name: { fr: "Pro", en: "Pro" },
    framing: { fr: "Pour les groupes et équipes en croissance", en: "For growing groups and teams" },
    tagline: { fr: "Pour piloter votre vie pro au quotidien.", en: "Run your business life, day to day." },
    cta: { fr: "Passer au Pro", en: "Get Pro" },
    features: {
      fr: ["Jusqu'à 3 entités / espaces de travail", "Espaces illimités", "Jusqu'à 30 collaborateurs", "Lecteurs illimités et gratuits", "Conseil IA : revues de performance", "300 crédits IA / mois", "10 automatisations", "100 Go de stockage"],
      en: ["Up to 3 entities / workspaces", "Unlimited spaces", "Up to 30 collaborators", "Unlimited free viewers", "AI Advisory: performance reviews", "300 AI credits / month", "10 automations", "100 GB storage"],
    },
  },
  {
    id: "business",
    price: 149,
    priceAnnual: 1430,
    priceLifetime: 3576,
    popular: true,
    limits: { entities: null, spaces: null, collaborators: 100, viewers: null, advisory: "full", aiCredits: 1500, automations: null, storageGb: 500, advancedPermissions: true, onboarding: false },
    name: { fr: "Business", en: "Business" },
    framing: { fr: "Pour les organisations multi-entités établies et grandes équipes", en: "For established multi-entity organizations and large teams" },
    tagline: { fr: "Toute la profondeur opérationnelle pour une équipe.", en: "Full operating depth for a team." },
    cta: { fr: "Choisir Business", en: "Choose Business" },
    features: {
      fr: ["Entités illimitées", "Espaces illimités", "Jusqu'à 100 collaborateurs", "Lecteurs illimités et gratuits", "Conseil IA : revues + board meetings", "1 500 crédits IA / mois", "Automatisations illimitées", "Permissions avancées", "500 Go de stockage"],
      en: ["Unlimited entities", "Unlimited spaces", "Up to 100 collaborators", "Unlimited free viewers", "AI Advisory: reviews + board meetings", "1,500 AI credits / month", "Unlimited automations", "Advanced permissions", "500 GB storage"],
    },
  },
  {
    id: "portfolio",
    price: 249,
    priceAnnual: 2390,
    priceLifetime: 5976,
    popular: false,
    limits: { entities: null, spaces: null, collaborators: null, viewers: null, advisory: "full", aiCredits: 5000, automations: null, storageGb: 1000, advancedPermissions: true, onboarding: true },
    name: { fr: "Portfolio", en: "Portfolio" },
    framing: { fr: "Pour les très grandes équipes multi-entités, sans limites", en: "For huge multi-entity teams, no limits" },
    tagline: { fr: "Pour piloter un groupe complet.", en: "Steer a full group." },
    cta: { fr: "Choisir Portfolio", en: "Choose Portfolio" },
    features: {
      fr: ["Entités illimitées", "Espaces illimités", "Collaborateurs illimités", "Lecteurs illimités et gratuits", "Conseil IA avancé : revues + board meetings", "5 000 crédits IA / mois", "Automatisations illimitées", "Permissions & rôles avancés", "Onboarding dédié", "1 To de stockage"],
      en: ["Unlimited entities", "Unlimited spaces", "Unlimited collaborators", "Unlimited free viewers", "Advanced AI Advisory: reviews + board meetings", "5,000 AI credits / month", "Unlimited automations", "Advanced permissions & roles", "Guided onboarding", "1 TB storage"],
    },
  },
  {
    id: "enterprise",
    price: null,
    priceAnnual: null,
    popular: false,
    limits: { entities: null, spaces: null, collaborators: null, viewers: null, advisory: "full", aiCredits: "custom", automations: null, storageGb: null, advancedPermissions: true, onboarding: true },
    name: { fr: "Enterprise", en: "Enterprise" },
    framing: { fr: "Système d'exploitation sur mesure", en: "Custom operating system" },
    tagline: { fr: "Adapté à votre organisation.", en: "Tailored to your organization." },
    cta: { fr: "Nous contacter", en: "Talk to us" },
    features: {
      fr: ["Tout le plan Portfolio", "Collaborateurs & crédits IA sur mesure", "Contrôle d'accès granulaire", "Workflows multiples + approbations", "LDAP / SSO", "Déploiements privés & managés", "Onboarding & support dédiés"],
      en: ["Everything in Portfolio", "Custom collaborators & AI credits", "Granular access control", "Multiple workflows + approvals", "LDAP / SSO", "Private + managed deployments", "Dedicated onboarding & support"],
    },
  },
];

export const priceLabel = (plan, lang, cycle = "monthly") => {
  if (plan.price === null) return { fr: "Sur devis", en: "Quote" }[lang];
  if (plan.price === 0) return { fr: "Gratuit", en: "Free" }[lang];
  if (cycle === "lifetime") return plan.priceLifetime ? `€${plan.priceLifetime}` : `€${plan.price}`;
  if (cycle === "annual") {
    const perMonth = Math.round(plan.priceAnnual / 12);
    return `€${perMonth}`;
  }
  return `€${plan.price}`;
};

// Total annual saving vs paying monthly (used to show "save €X" badges).
export const annualSaving = (plan) => {
  if (!plan.price || plan.price === 0 || plan.priceAnnual === null) return 0;
  return plan.price * 12 - plan.priceAnnual;
};

// Annual discount as a percentage off the monthly run-rate (e.g. 17% for "2 months free").
export const annualPercent = (plan) => {
  if (!plan.price || plan.price === 0 || !plan.priceAnnual) return 0;
  return Math.round((1 - plan.priceAnnual / (plan.price * 12)) * 100);
};

// Headline annual discount (derived from Pro) — used by billing toggles.
export const ANNUAL_DISCOUNT_PCT = annualPercent(PLANS.find((p) => p.id === "pro")) || 20;

// Limited-time lifetime launch: the displayed price is already 50% off; the struck-through
// "before" anchor = price × 2, and the cap is the first 100 buyers.
export const LIFETIME_DISCOUNT_PCT = 50;
export const LIFETIME_CAP = 100;
export const lifetimeBefore = (plan) => (plan.priceLifetime ? plan.priceLifetime * 2 : null);

export const collaboratorsLabel = (plan, lang) => {
  const n = plan.limits.collaborators;
  if (n === null) return { fr: "Collaborateurs illimités", en: "Unlimited collaborators" }[lang];
  if (n === 0) return { fr: "Collaboration solo", en: "Solo workspace" }[lang];
  return { fr: `${n} collaborateurs inclus`, en: `${n} collaborators included` }[lang];
};

export const advisoryLabel = (level, lang) => ({
  false: { fr: "—", en: "—" },
  review: { fr: "Revues", en: "Reviews" },
  full: { fr: "Revues + Board", en: "Reviews + Board" },
}[level === false ? "false" : level][lang]);

